import React, { useEffect, useMemo } from 'react';
import { Col, Row, theme } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import GapsAuditFindingsTable from '../GapsAuditFindingsTable';
import { gapsOp } from 'store/ducks/Gaps';
import { IAuditData, IAudits, IAuditFindingRow } from 'store/ducks/Gaps/types';
import Helpers from 'utils/Helpers';
import { AuditFindingColumns } from 'constants/tableColumns';
import * as Styled from './AuditFindingsTable.styled';

const { useToken } = theme;

const AuditFindingsTable: React.FC = () => {
  const dispatch = useDispatch();
  const { token } = useToken();
  const {
    lang,
    drawTable,
    auditFindingMapping,
    assignedAuditFindingsUpdated,
    loading,
    scheme,
    orgContext
  } = useSelector((state: RootState) => ({
    lang: state.global.lang,
    drawTable: state.complianceEst.drawTable,
    auditFindingMapping: state.gaps.auditFindingMapping,
    assignedAuditFindingsUpdated: state.gaps.assignedAuditFindingsUpdated,
    loading: state.gaps.loading,
    scheme: state.global.scheme,
    orgContext: state.global.hierarchicalLevelsScope
  }));
  const filterParams = Helpers.toApiFilters(orgContext);

  // Deletes an audit finding and refreshes the data
  const fetchDeleteAudit = async (audit: IAudits) => {
    dispatch(gapsOp.setAssignedAuditFindingsUpdated(false));
    try {
      const params = { afg_id: audit.afg_id };
      await gapsOp.deleteAudits(params);
      dispatch(gapsOp.setAssignedAuditFindingsUpdated(true));
    } catch (error) {
      console.error('Failed to fetch audits:', error);
    }
  };

  const auditFindingColumns = AuditFindingColumns({
    scheme,
    token,
    fetchDeleteAudit
  });
  const buildRows = (data: IAuditData[]): IAuditFindingRow[] => {
    return data.map((item) => ({
      key: String(item.id),
      id: item.id,
      value: item.value,
      category: item.category,
      audits: item.AUDITS ?? [],
      originalItem: item
    }));
  };

  const dataSource = useMemo(
    () => buildRows(auditFindingMapping ?? []),
    [auditFindingMapping]
  );

  // Fetch data when component mounts or when drawTable becomes true

  useEffect(() => {
    const hasFilters = Object.keys(filterParams).length > 0;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    if (!hasFilters) return;

    if (drawTable) {
      gapsOp
        .fetchAuditFindingsMapping(lang, filterParams)
        .then((response) => {
          dispatch(gapsOp.setAuditFindingsMapping(response));
        })
        .catch((error) => {
          console.error('Failed to fetch audit findings mapping:', error);
        });
    }
  }, [lang, dispatch, drawTable]);

  // Refetch data when assigned audit findings are updated
  useEffect(() => {
    const hasFilters = Object.keys(filterParams).length > 0;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    if (!hasFilters) return;

    if (assignedAuditFindingsUpdated) {
      gapsOp
        .fetchAuditFindingsMapping(lang, filterParams)
        .then((response) => {
          dispatch(gapsOp.setAuditFindingsMapping(response));
          dispatch(gapsOp.setAssignedAuditFindingsUpdated(false));
        })
        .catch((error) => {
          console.error('Failed to fetch audit findings mapping:', error);
          dispatch(gapsOp.setAssignedAuditFindingsUpdated(false));
        });
    }
  }, [assignedAuditFindingsUpdated, dispatch, lang, filterParams]);

  return (
    <Row>
      <>
        <Col lg={12}>
          <Styled.StyledCustomTable
            drawTable={true}
            token={token}
            columns={auditFindingColumns}
            dataSource={dataSource}
            itemsCount={dataSource.length}
            loading={loading}
          />
        </Col>
        <Col lg={12}>
          <GapsAuditFindingsTable />
        </Col>
      </>
    </Row>
  );
};

export default AuditFindingsTable;
